import React, { useState, useEffect, lazy, Suspense } from 'react';
import ExifReader from 'exifreader';
import { clsx } from 'clsx';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';
import SafeIcon from './common/SafeIcon';
import DropZone from './components/DropZone';
import Footer from './components/Footer';
import Benefits from './components/Benefits';
import FAQ from './components/FAQ';
import SocialShare from './components/SocialShare';
import { analyzeForensics } from './utils/forensics';

const ExifView = lazy(() => import('./components/ExifView'));
const ForensicsReport = lazy(() => import('./components/ForensicsReport'));

function App() {
  const [file, setFile] = useState(null);
  const [exifData, setExifData] = useState(null);
  const [forensicsData, setForensicsData] = useState(null);
  const [isForensicsMode, setIsForensicsMode] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleFileSelect = async (selectedFile) => {
    try {
      setFile(selectedFile);
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      const tags = await ExifReader.load(selectedFile);
      setExifData(tags);
      const report = analyzeForensics(selectedFile, tags);
      setForensicsData(report);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-950 text-slate-100 font-sans selection:bg-blue-500/30">
      <header className="bg-slate-900/90 backdrop-blur-xl border-b border-slate-800 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-blue-600 text-white p-2 rounded-lg shadow-[0_0_15px_rgba(37,99,235,0.4)]">
              <SafeIcon icon={FiIcons.FiCpu} className="text-xl" />
            </div>
            <h1 className="text-xl font-black tracking-tighter">AI<span className="text-blue-500">INVESTIGATOR</span></h1>
          </div>
          
          <div className="flex items-center gap-3">
            <span className={clsx("text-[10px] font-black uppercase tracking-widest transition", isForensicsMode ? "text-red-500" : "text-slate-500")}>
              Forensic Mode
            </span>
            <button 
              onClick={() => setIsForensicsMode(!isForensicsMode)}
              className={clsx(
                "relative inline-flex h-5 w-10 items-center rounded-full transition-colors",
                isForensicsMode ? "bg-red-600/50 border border-red-500/50" : "bg-slate-800 border border-slate-700"
              )}
            >
              <span className={clsx("inline-block h-3 w-3 transform rounded-full bg-white transition shadow-md", isForensicsMode ? "translate-x-6" : "translate-x-1")} />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-grow max-w-6xl w-full mx-auto px-4 py-8">
        {!file ? (
          <div className="max-w-4xl mx-auto py-12">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6 font-bold uppercase tracking-[0.2em] text-blue-400 text-[10px]">
                <SafeIcon icon={FiIcons.FiShield} /> takedowntaskforce.com
              </div>
              <h2 className="text-5xl font-black text-white mb-4 tracking-tighter leading-none">AI PHOTO INVESTIGATOR</h2>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto">Neural-pattern analysis and pixel veracity auditing to detect Midjourney, DALL-E, and AI-edited content.</p>
            </div>
            <div className="bg-slate-900/40 p-3 rounded-3xl border border-slate-800 backdrop-blur-md shadow-2xl">
              <DropZone onFileSelect={handleFileSelect} />
            </div>
            <Benefits />
            <FAQ />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-slate-900 rounded-3xl border border-slate-800 shadow-2xl overflow-hidden sticky top-24">
                <div className="aspect-square bg-black border-b border-slate-800">
                  <img src={previewUrl} alt="Preview" className="w-full h-full object-contain" />
                </div>
                
                <div className="p-6 space-y-6">
                  <div>
                    <div className="flex justify-between items-end mb-2">
                      <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Neural Classification</p>
                      <span className={clsx("text-[10px] font-black uppercase px-2 py-0.5 rounded", forensicsData?.scores.color)}>
                        {forensicsData?.scores.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-4xl font-black tracking-tighter text-white">{forensicsData?.scores.risk}%</span>
                      <div className="flex-1 bg-slate-800 h-2 rounded-full overflow-hidden">
                        <div 
                          className={clsx("h-full transition-all duration-1000", forensicsData?.scores.risk > 50 ? "bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.4)]" : "bg-blue-500")}
                          style={{ width: `${forensicsData?.scores.risk}%` }}
                        />
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-2 font-medium">Risk Factor: {forensicsData?.scores.risk}% AI Probability</p>
                  </div>

                  <div className="grid grid-cols-2 gap-y-4 gap-x-6 pt-4 border-t border-slate-800">
                    <div>
                      <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Time Audit</p>
                      <p className="text-xs font-bold text-slate-200">
                        {forensicsData?.timestamps.original ? format(forensicsData.timestamps.original, 'yyyy-MM-dd') : 'No Record'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Tag Credit</p>
                      <p className="text-xs font-bold text-blue-400 uppercase truncate">
                        {forensicsData?.rawTags?.Software ? `Software: ${forensicsData.rawTags.Software}` : 
                         forensicsData?.rawTags?.Make ? `Device: ${forensicsData.rawTags.Make}` : 'None Detected'}
                      </p>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-slate-800">
                     <SocialShare fileName={file.name} veracity={forensicsData?.scores.veracity} />
                  </div>

                  <button 
                    onClick={() => setFile(null)}
                    className="w-full mt-4 flex items-center justify-center gap-2 text-slate-400 bg-slate-800/50 hover:bg-slate-800 py-3 rounded-xl transition text-[10px] font-black uppercase tracking-widest border border-slate-700/50"
                  >
                    <SafeIcon icon={FiIcons.FiRefreshCw} /> New Investigation
                  </button>
                </div>
              </div>
            </div>

            <div className="lg:col-span-8">
              <Suspense fallback={<div className="h-64 flex items-center justify-center bg-slate-900 rounded-3xl border border-slate-800"><SafeIcon icon={FiIcons.FiLoader} className="text-4xl text-blue-500 animate-spin" /></div>}>
                {isForensicsMode ? (
                  <ForensicsReport report={forensicsData} fileName={file.name} />
                ) : (
                  <ExifView tags={exifData} />
                )}
              </Suspense>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default App;